/**
 * Upgrade Paywall Modal - Clay-Inspired Premium UI with IDR Pricing
 */

import React, { useState } from 'react';
import { X, Sparkles, Crown, Check, Zap, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 30 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 280, damping: 22 }
  },
  exit: { opacity: 0, scale: 0.92, y: 30 }
};

const buttonVariants = {
  tap: { scale: 0.96 },
  hover: { scale: 1.02, y: -2 }
};

export default function UpgradeModal({ isOpen, onClose, onUpgrade, lockedCount, planType }) {
  const { userId } = useUser();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const plans = [
    {
      id: 'basic',
      name: 'Swoopy Basic',
      price: 'Rp49.000',
      icon: Zap,
      color: '#F59E0B',
      bgColor: 'rgba(255, 183, 77, 0.15)',
      features: [
        'Premium itinerary unlock',
        'Limited AI recommendations',
        'Downloadable travel summary'
      ],
      cta: 'Unlock Basic',
      popular: false
    },
    {
      id: 'plus',
      name: 'Swoopy Plus',
      price: 'Rp99.000',
      icon: Crown,
      color: 'var(--swoopy-pink)',
      bgColor: 'var(--swoopy-pink-soft)',
      features: [
        'Full premium itinerary unlock',
        'Advanced AI recommendations',
        'Unlimited premium trips',
        'Hidden destination suggestions',
        'Premium export access'
      ],
      cta: 'Unlock Plus',
      popular: true
    }
  ];

  const handleCheckout = async (planId) => {
    setLoading(planId);
    setError(null);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: planId,
          user_id: userId,
          origin_url: originUrl
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Gagal membuat sesi pembayaran');
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL pembayaran tidak diterima');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(46, 46, 46, 0.5)', backdropFilter: 'blur(10px)' }}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div 
            className="relative w-full max-w-md rounded-3xl p-6 overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{ 
              backgroundColor: 'var(--card-bg)', 
              boxShadow: 'var(--shadow-clay-lg)'
            }}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            data-testid="upgrade-modal"
          >
            {/* Inner highlight */}
            <div 
              className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
              style={{ 
                background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)',
                borderRadius: '24px 24px 0 0'
              }}
            />

            {/* Close button */}
            <motion.button
              onClick={onClose}
              className="absolute top-4 right-4 p-2.5 rounded-xl transition-colors"
              style={{ 
                backgroundColor: 'var(--clay-white)',
                boxShadow: 'var(--shadow-clay-xs)'
              }}
              whileTap={{ scale: 0.95 }}
              whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
              data-testid="upgrade-modal-close"
            >
              <X size={18} style={{ color: 'var(--text-secondary)' }} />
            </motion.button>

            {/* Header */}
            <div className="text-center mb-6 relative z-10">
              <motion.div 
                className="w-18 h-18 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                style={{ 
                  width: '72px',
                  height: '72px',
                  background: 'linear-gradient(145deg, var(--swoopy-pink-soft), rgba(233, 116, 143, 0.2))',
                  boxShadow: 'var(--shadow-clay-md)'
                }}
                initial={{ scale: 0.8, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 300, delay: 0.1 }}
              >
                <Sparkles size={34} style={{ color: 'var(--swoopy-pink)' }} />
              </motion.div>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: '600', 
                color: 'var(--text-primary)', 
                marginBottom: '8px',
                fontFamily: "'Fredoka', system-ui, sans-serif",
                letterSpacing: '0.01em'
              }}>
                Unlock Premium
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {lockedCount > 0 
                  ? `${lockedCount} trip lagi menunggumu!`
                  : "Kamu telah mencapai batas gratis"}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-xl text-center text-sm"
                style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
              >
                {error}
              </motion.div>
            )}

            {/* Plan Cards */}
            <div className="space-y-3 mb-6 relative z-10">
              {plans.map((plan, index) => {
                const Icon = plan.icon;
                const isCurrentPlan = planType === plan.id;
                const isLoading = loading === plan.id;
                
                return (
                  <motion.div
                    key={plan.id}
                    className="relative p-4 rounded-2xl transition-all"
                    style={{ 
                      backgroundColor: 'var(--clay-white)',
                      border: plan.popular ? '2px solid var(--swoopy-pink)' : 'none',
                      boxShadow: 'var(--shadow-clay-sm)'
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + index * 0.1, type: 'spring' }}
                  >
                    {/* Inner highlight */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                      style={{ 
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)',
                        borderRadius: '14px 14px 0 0'
                      }}
                    />
                    
                    {plan.popular && (
                      <span 
                        className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-extrabold text-white"
                        style={{ 
                          background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))',
                          boxShadow: 'var(--shadow-clay-btn)' 
                        }}
                      >
                        Paling Populer
                      </span>
                    )}
                    
                    <div className="flex items-start gap-3 relative z-10">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ 
                          backgroundColor: plan.bgColor,
                          boxShadow: 'var(--shadow-clay-xs)'
                        }}
                      >
                        <Icon size={24} style={{ color: plan.color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold" style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
                            {plan.name}
                          </h3>
                          <span className="font-semibold text-lg" style={{ color: plan.color }}>
                            {plan.price}
                          </span>
                        </div>
                        
                        <ul className="space-y-1.5">
                          {plan.features.slice(0, 2).map((feature, i) => (
                            <li 
                              key={i}
                              className="flex items-center gap-2 text-xs font-medium"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              <Check size={14} style={{ color: plan.color }} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <motion.button
                      onClick={() => handleCheckout(plan.id)}
                      disabled={isCurrentPlan || loading !== null}
                      className="w-full mt-4 h-11 rounded-xl text-sm font-bold transition-all relative overflow-hidden"
                      style={{ 
                        background: isCurrentPlan 
                          ? 'var(--clay-white)' 
                          : plan.popular 
                            ? 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink), var(--swoopy-pink-dark))'
                            : plan.bgColor,
                        color: isCurrentPlan ? 'var(--text-muted)' : plan.popular ? 'white' : plan.color,
                        opacity: (isCurrentPlan || (loading && !isLoading)) ? 0.6 : 1,
                        boxShadow: plan.popular && !isCurrentPlan ? 'var(--shadow-clay-btn)' : 'var(--shadow-clay-xs)'
                      }}
                      variants={buttonVariants}
                      whileTap={!isCurrentPlan && !loading ? "tap" : undefined}
                      whileHover={!isCurrentPlan && !loading ? "hover" : undefined}
                      data-testid={`upgrade-${plan.id}-btn`}
                    >
                      {/* Inner highlight for button */}
                      {plan.popular && !isCurrentPlan && (
                        <div 
                          className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                          style={{ 
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)'
                          }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {isLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Memproses...
                          </>
                        ) : isCurrentPlan ? (
                          'Paket Saat Ini'
                        ) : (
                          plan.cta
                        )}
                      </span>
                    </motion.button>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer note */}
            <p 
              className="text-center text-xs font-medium relative z-10"
              style={{ color: 'var(--swoopy-pink)' }}
            >
              Pembayaran diproses dalam Rupiah (IDR)
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
